/**
 * Firestore Observable Helpers
 * Provides Observable-based wrappers around core Firebase Firestore SDK
 * Replaces AngularFire's collectionData and docData convenience methods
 */
import {
  collection,
  doc,
  onSnapshot,
  query as firestoreQuery,
  DocumentData,
  Query,
  Firestore,
  DocumentReference
} from 'firebase/firestore';
import { Observable } from 'rxjs';

/**
 * Converts a Firestore query to an Observable of typed documents
 * @param fs Firestore instance
 * @param queryFn Function that returns a Query
 * @param options Optional config for id field
 * @returns Observable of typed document array
 */
export function collectionDataObservable<T = DocumentData>(
  fs: Firestore,
  queryFn: (fs: Firestore) => Query<DocumentData>,
  options?: { idField?: string }
): Observable<T[]> {
  return new Observable<T[]>((subscriber) => {
    try {
      const firestoreQuery = queryFn(fs);
      const unsubscribe = onSnapshot(
        firestoreQuery,
        (querySnapshot) => {
          const data: T[] = [];
          querySnapshot.forEach((doc) => {
            const docData = doc.data() as T;
            if (options?.idField) {
              (docData as any)[options.idField] = doc.id;
            }
            data.push(docData);
          });
          subscriber.next(data);
        },
        (error) => {
          subscriber.error(error);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      subscriber.error(error);
      return () => undefined;
    }
  });
}

/**
 * Converts a Firestore document to an Observable of typed data
 * @param fs Firestore instance
 * @param path Document path
 * @param options Optional config for id field
 * @returns Observable of typed document data or undefined
 */
export function docDataObservable<T = DocumentData>(
  fs: Firestore,
  path: string,
  options?: { idField?: string }
): Observable<T | undefined> {
  return new Observable<T | undefined>((subscriber) => {
    try {
      const docRef = doc(fs, path);
      const unsubscribe = onSnapshot(
        docRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data() as T;
            if (options?.idField) {
              (data as any)[options.idField] = docSnapshot.id;
            }
            subscriber.next(data);
          } else {
            subscriber.next(undefined);
          }
        },
        (error) => {
          subscriber.error(error);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      subscriber.error(error);
      return () => undefined;
    }
  });
}

/**
 * Converts a document reference to an Observable of typed data
 * @param docRef Document reference
 * @param options Optional config for id field
 * @returns Observable of typed document data or undefined
 */
export function docRefObservable<T = DocumentData>(
  docRef: DocumentReference<DocumentData>,
  options?: { idField?: string }
): Observable<T | undefined> {
  return new Observable<T | undefined>((subscriber) => {
    try {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data() as T;
            if (options?.idField) {
              (data as any)[options.idField] = docSnapshot.id;
            }
            subscriber.next(data);
          } else {
            subscriber.next(undefined);
          }
        },
        (error) => {
          subscriber.error(error);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      subscriber.error(error);
      return () => undefined;
    }
  });
}
